/** Booking queries. */

import { keepPreviousData, type UseQueryResult, useQuery } from '@tanstack/react-query';
import { queryKeys } from '../query-keys';
import { useApiClient } from '../react/ApiClientProvider';
import {
  type Booking,
  type BookingStatus,
  bookingSchema,
  type Paginated,
  paginated,
} from '../responses';
import { unwrap } from './_helpers';

/** Server-side filter/search/pagination params for the bookings list. */
export type BookingsFilters = {
  status?: BookingStatus;
  confirmationMode?: 'manual' | 'pms';
  /** Free-text match against id / room type / external PMS id. */
  q?: string;
  /** Check-in on/after (YYYY-MM-DD). */
  from?: string;
  /** Check-in on/before (YYYY-MM-DD). */
  to?: string;
  limit?: number;
  offset?: number;
};

/** `GET /bookings` — filtered, searched, paginated bookings (newest first). */
export function useBookings(
  tenantId: string,
  hotelId: string,
  filters?: BookingsFilters,
): UseQueryResult<Paginated<Booking>> {
  const client = useApiClient();
  return useQuery({
    queryKey: queryKeys.bookings(tenantId, hotelId, filters),
    placeholderData: keepPreviousData,
    queryFn: async () => {
      const result = await client.GET('/api/v1/tenants/{tenantId}/hotels/{hotelId}/bookings', {
        params: {
          path: { tenantId, hotelId },
          query: {
            status: filters?.status,
            confirmationMode: filters?.confirmationMode,
            q: filters?.q,
            from: filters?.from,
            to: filters?.to,
            limit: filters?.limit,
            offset: filters?.offset,
          },
        },
        parseAs: 'json',
      });
      return unwrap(result, paginated(bookingSchema));
    },
  });
}

/** `GET /bookings/:bookingId`. */
export function useBooking(
  tenantId: string,
  hotelId: string,
  bookingId: string,
): UseQueryResult<Booking> {
  const client = useApiClient();
  return useQuery({
    queryKey: queryKeys.booking(tenantId, hotelId, bookingId),
    queryFn: async () => {
      const result = await client.GET(
        '/api/v1/tenants/{tenantId}/hotels/{hotelId}/bookings/{bookingId}',
        { params: { path: { tenantId, hotelId, bookingId } }, parseAs: 'json' },
      );
      return unwrap(result, bookingSchema);
    },
    enabled: bookingId.length > 0,
  });
}
